// src/Pages/LandingPage/LandingPage.js
import React, { useMemo } from "react";
import "./LandingPage.css";
import BackgroundLottie from "../../Components/BackgroundLottie/BackgroundLottie";
// eslint-disable-next-line
import { useGasApi } from "../../utils/useGasApi";
import { getStatus } from "../../utils/schedule";
import { useContent, useSettings, useFeaturedEvents, assetUrl } from "../../utils/useContent";
import SubscribeForm from "../../Components/SubscribeForm/SubscribeForm";
import SEO from "../../Components/SEO/SEO";
import logo from "../../assets/logo.webp"; //
import socceroofLogo from "../../assets/Partners/socceroofLogo.jpg"
import slcLogo from "../../assets/Partners/slcLogo.jpg"
import concordiaLogo from "../../assets/Partners/concordiaLogo.jpeg"
import electrolitLogo from "../../assets/Partners/electrolit.jpeg";
import slippioLogo from "../../assets/Partners/Slippio.jpeg";

/* ---------- GAS endpoint + key ---------- */
const GAS_URL =
  "https://script.google.com/macros/s/AKfycby6kZbzYtg1LcHHO17It_0_odwUFNAUxg2ybQgTmiHDj5LB11-Dmyy1PlIWoAA--NnZ/exec";

let GAS_KEY = "asx_2025_api_9a7c";
try {
  if (typeof import.meta !== "undefined" && import.meta.env && import.meta.env.VITE_GAS_KEY) {
    GAS_KEY = import.meta.env.VITE_GAS_KEY;
  } else if (typeof process !== "undefined" && process.env && process.env.VITE_GAS_KEY) {
    GAS_KEY = process.env.VITE_GAS_KEY;
  }
} catch (_) {}

/* ---------- date helpers ---------- */
function pad(n) {
  return n < 10 ? `0${n}` : String(n);
}
function parseLocalStart(dateStr, timeStr) {
  const date = String(dateStr || "").trim();
  const time = String(timeStr || "").trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [y, m, d] = date.split("-").map(Number);
    const [hh, mm] = (time && /^\d{1,2}:\d{2}$/.test(time) ? time : "12:00")
      .split(":")
      .map(Number);
    return new Date(
      y,
      m - 1,
      d,
      isNaN(hh) ? 12 : hh,
      isNaN(mm) ? 0 : mm,
      0,
      0
    );
  }
  const tryA = new Date(`${date} ${time || "12:00"}`);
  if (!isNaN(tryA)) return tryA;
  const n = new Date();
  return new Date(n.getFullYear(), n.getMonth(), n.getDate(), 12, 0, 0, 0);
}
function dateKeyLocal(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function fmtDate(d) {
  try {
    return d.toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}
function fmtTime(t = "") {
  return t?.toUpperCase?.() || t;
}
const byStart = (a, b) => (a?._start || 0) - (b?._start || 0);

/* ---------- Smooth, seamless ticker ---------- */
function Ticker({ items = [], durationSec = 28 }) {
  return (
    <div className="ticker" role="region" aria-label="ASX news & contacts">
      <div className="ticker-track" style={{ "--dur": `${durationSec}s` }}>
        {/* Group A */}
        <ul className="ticker-group">
          {items.map((it, i) => (
            <li className="ticker-item" key={`a-${i}`}>
              {it.href ? (
                <a
                  href={it.href}
                  target={it.external ? "_blank" : undefined}
                  rel="noreferrer"
                >
                  {it.icon ? (
                    <span className="ti-ico" aria-hidden>
                      {it.icon}
                    </span>
                  ) : null}
                  <span className="ti-text">{it.text}</span>
                </a>
              ) : (
                <>
                  {it.icon ? (
                    <span className="ti-ico" aria-hidden>
                      {it.icon}
                    </span>
                  ) : null}
                  <span className="ti-text">{it.text}</span>
                </>
              )}
            </li>
          ))}
        </ul>
        {/* Group B (duplicate) */}
        <ul className="ticker-group" aria-hidden="true">
          {items.map((it, i) => (
            <li className="ticker-item" key={`b-${i}`}>
              {it.href ? (
                <a
                  href={it.href}
                  target={it.external ? "_blank" : undefined}
                  rel="noreferrer"
                >
                  {it.icon ? (
                    <span className="ti-ico" aria-hidden>
                      {it.icon}
                    </span>
                  ) : null}
                  <span className="ti-text">{it.text}</span>
                </a>
              ) : (
                <>
                  {it.icon ? (
                    <span className="ti-ico" aria-hidden>
                      {it.icon}
                    </span>
                  ) : null}
                  <span className="ti-text">{it.text}</span>
                </>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/* ---------- Google Maps background (no key) ---------- */
// eslint-disable-next-line
function GMapBackdrop({ placeQuery }) {
  const q = encodeURIComponent(placeQuery);
  const src = `https://www.google.com/maps?q=${q}&output=embed&z=15`;
  return (
    <div className="gmap-bg-wrapper" aria-hidden>
      <iframe
        className="gmap-bg"
        src={src}
        loading="lazy"
        allowFullScreen
        referrerPolicy="no-referrer-when-downgrade"
        title="Venue Map"
      />
      <div className="gmap-scrim" />
      <div className="gmap-blur" />
    </div>
  );
}

/* ---------- Why Join deck ---------- */
const WHY_FALLBACK = [
  { title: "CASH PRIZE", description: "Winning teams are awarded a cash prize in recognition of their achievement.", image_path: "/img/why/cash-prize.jpg" },
  { title: "INDIVIDUAL AWARDS", description: "Awards for top-performing players are given out throughout the season with various gifts from our sponsors.", image_path: "/img/why/individual-awards.jpg" },
  { title: "PROFESSIONAL PHOTOS & VIDEOS", description: "Our team ensures professional photography & videography for all our players throughout all games of the season.", image_path: "/img/why/pro-photo.jpg" },
  { title: "XPERIENCE THE COMMUNITY", description: "At ASX, you’re not just playing. You’re joining a community built on fair play, passion, and lasting soccer bonds.", image_path: "/img/why/community.jpg" },
];

function WhyJoinDeck() {
  const { items } = useContent("why-items", WHY_FALLBACK);

  const card = {
    background: "#fff",
    border: "1px solid rgba(0,0,0,.08)",
    borderRadius: 18,
    boxShadow: "0 12px 32px rgba(0,0,0,.06)",
    padding: 18,
    textAlign: "center",
  };
  const imgWrap = {
    width: "100%",
    aspectRatio: "1 / 1",
    overflow: "hidden",
    borderRadius: 16,
    border: "1px solid rgba(0,0,0,.08)",
    background: "#f8fafc",
  };
  const img = {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  };
  const h = {
    margin: "16px 0 6px",
    fontSize: 20,
    fontWeight: 900,
    letterSpacing: ".2px",
  };
  const p = { margin: 0, color: "#475569" };

  return (
    <div className="why-deck-grid">
      {items.map((it, i) => {
        const src = it.image_path
          ? (it.image_path.startsWith("/img/") ? it.image_path : assetUrl(it.image_path))
          : "";
        return (
          <article key={it.id ?? i} style={card}>
            <div style={imgWrap}>
              {src ? <img src={src} alt={it.title} loading="lazy" style={img} /> : null}
            </div>
            <h3 style={h}>{it.title}</h3>
            <p style={p}>{it.description ?? it.desc}</p>
          </article>
        );
      })}
    </div>
  );
}


/* ---------- Reviews carousel ---------- */
function ReviewsCarousel() {
  const PLACE_NAME = "All Star Xperience";
  const REVIEWS_LINK =
    "https://www.google.com/search?sca_esv=f428b6d1b594cc50&sxsrf=AE3TifOIvGiChlGqTfX0pzqFo-z0kyhgzA:1762546067361&si=AMgyJEtREmoPL4P1I5IDCfuA8gybfVI2d5Uj7QMwYCZHKDZ-EweG36-4pw-u2iaOc_ZnOg_pmJCsB4E3dB6Medmak-KxSzJ94Zy2B_32raoq2q12uj89PfFAn0FktQrHtM66HRSu_28ScQ8dTRfdJJYYa0GJyJpTaA%3D%3D&q=All+Star+Xperience+Reviews&sa=X&ved=2ahUKEwjG9frh6-CQAxVtKFkFHeU2GD0Q0bkNegQINBAD&biw=1440&bih=778&dpr=2";
  const WRITE_REVIEW_LINK =
    "https://www.google.com/search?q=All+Star+Xperience+Montreal+write+a+review#lrd=0x8fb0d1004bd32d53:0x8308df091030c33,3,,,,";

  const REVIEWS = [
    {
      author_name: "Hadi Harati",
      rating: 5,
      relative_time_description: "9 months ago",
      text: "Incredible platform—professional, welcoming, unforgettable energy and rewards.",
      source_url: REVIEWS_LINK,
    },
    {
      author_name: "Abdoul",
      rating: 5,
      relative_time_description: "5 months ago",
      text: "Top-tier events, super organized with kind, clear communication.",
      source_url: REVIEWS_LINK,
    },
    {
      author_name: "L'IGIWANITO",
      rating: 5,
      relative_time_description: "6 months ago",
      text: "Great event—back next tournament. Shout-out to Talal and Jad!",
      source_url: REVIEWS_LINK,
    },
    {
      author_name: "Samy Haddad",
      rating: 5,
      relative_time_description: "9 months ago",
      text: "Owners truly care; amazing community vibe—one of Montréal’s best.",
      source_url: REVIEWS_LINK,
    },
    {
      author_name: "Younna Bissat",
      rating: 5,
      relative_time_description: "9 months ago",
      text: "Fun competition, prizes, and a great way to meet new people.",
      source_url: REVIEWS_LINK,
    },
    {
      author_name: "Adam Elias",
      rating: 5,
      relative_time_description: "8 months ago",
      text: "Amazing organization and level—perfect for competitive players.",
      source_url: REVIEWS_LINK,
    },
    {
      author_name: "Abdellah Alturahi",
      rating: 5,
      relative_time_description: "9 months ago",
      text: "One of my best experiences—impeccable vibes and community spirit.",
      source_url: REVIEWS_LINK,
    },
    {
      author_name: "Joseph Moarbes",
      rating: 5,
      relative_time_description: "6 months ago",
      text: "Very well-organized tournaments in Montréal. The best.",
      source_url: REVIEWS_LINK,
    },
    {
      author_name: "karim kanawati",
      rating: 5,
      relative_time_description: "9 months ago",
      text: "Great people and professional staff. I’ll participate again.",
      source_url: REVIEWS_LINK,
    },
    {
      author_name: "Sam Ghattas",
      rating: 5,
      relative_time_description: "9 months ago",
      text: "Amazing tournament—very well organized.",
      source_url: REVIEWS_LINK,
    },
  ];

  const LOOPS = [...REVIEWS, ...REVIEWS];
  const avg =
    (REVIEWS.reduce((s, r) => s + (r.rating || 0), 0) / REVIEWS.length) || 5;

  return (
    <section className="section section-tight" id="reviews">
      <div className="container">
        <div className="gr-wrap card">
          <div className="gr-header">
            <h2 className="h2">What players say</h2>
            <div className="gr-cta">
              <a
                className="gr-write"
                href={WRITE_REVIEW_LINK}
                target="_blank"
                rel="noreferrer"
                aria-label="Write a Google review"
                title="Write a Google review"
              >
                <svg
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  aria-hidden="true"
                >
                  <path
                    fill="currentColor"
                    d="M12 17.3 6.2 20l1.1-6.5L2 8.9l6.6-1L12 2l3.4 5.9 6.6 1-5.3 4.6L17.8 20z"
                  />
                </svg>
                <span>Write a review</span>
              </a>

              <a
                className="gr-badge"
                href={REVIEWS_LINK}
                target="_blank"
                rel="noreferrer"
                aria-label="Open Google reviews"
              >
                <span className="gr-logo" aria-hidden>
                  G
                </span>
                <span className="gr-text">
                  <strong>{PLACE_NAME}</strong>
                  <em>
                    {avg.toFixed(1)} ★ • {REVIEWS.length + 15}+
                  </em>
                </span>
              </a>
            </div>
          </div>

          <div className="gr-carousel" aria-label="Player reviews">
            <div className="gr-fade gr-fade--left" aria-hidden />
            {/* eslint-disable-next-line */}
            <ul className="gr-track" role="list">
              {LOOPS.map((r, i) => {
                const full = Math.max(0, Math.min(5, r.rating || 0));
                const initials = getInitials(r.author_name || "U");
                const colorClass = pickColorClass(r.author_name || "U");
                return (
                  <li className="gr-card" key={`${r.author_name}-${i}`}>
                    <div className="gr-row">
                      <div
                        className={`gr-avatar ${colorClass}`}
                        title={r.author_name}
                      >
                        {initials}
                      </div>
                      <div className="gr-meta">
                        <div className="gr-author">{r.author_name}</div>
                        <div
                          className="gr-stars"
                          aria-label={`${r.rating} out of 5 stars`}
                        >
                          {"★★★★★".slice(0, full)}
                          <span className="gr-dim">
                            {"★★★★★".slice(full)}
                          </span>
                        </div>
                        <div className="gr-time">
                          {r.relative_time_description}
                        </div>
                      </div>
                    </div>
                    <p className="gr-textline">“{r.text}”</p>
                    <div className="gr-actions-row">
                      <a
                        className="gr-read"
                        href={r.source_url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Read on Google ↗
                      </a>
                    </div>
                  </li>
                );
              })}
            </ul>
            <div className="gr-fade gr-fade--right" aria-hidden />
          </div>

          <div className="gr-footer-note">Verified via Google.</div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Footer ---------- */
function Footer() {
  const year = new Date().getFullYear();

  const links = [
    { label: "Home", href: "/" },
    { label: "Leagues", href: "/leagues" },
    { label: "Hall of Fame", href: "/hall-of-fame" },
    { label: "Rules", href: "/rules" },
    { label: "About", href: "/about" },
    { label: "Register", href: "/register" },
  ];

  return (
    <footer className="site-footer" role="contentinfo">
      <div className="footer-top container">
        {/* Logo only (left) */}
        <div className="ft-col ft-brand">
          <div className="ft-logo">
            <img
              src={logo}
              alt="All Star Xperience"
              className="ft-logo-img"
              width="220"
              height="200"
            />
          </div>

          <div className="ft-social">
            <a
              className="ft-social-btn"
              href="https://www.instagram.com/allstarxperience/#"
              target="_blank"
              rel="noreferrer"
              aria-label="Instagram"
              title="Instagram"
            >
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                aria-hidden="true"
              >
                <path
                  fill="currentColor"
                  d="M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5Zm0 2a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3H7Zm5 3.5A5.5 5.5 0 1 1 6.5 13 5.5 5.5 0 0 1 12 7.5Zm0 2A3.5 3.5 0 1 0 15.5 13 3.5 3.5 0 0 0 12 9.5Zm6-2.25a1.25 1.25 0 1 1-1.25 1.25A1.25 1.25 0 0 1 18 7.25Z"
                />
              </svg>
              <span>Instagram</span>
            </a>

            <a
              className="ft-social-btn"
              href="https://www.facebook.com/allstarxperience"
              target="_blank"
              rel="noreferrer"
              aria-label="Facebook"
              title="Facebook"
            >
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                aria-hidden="true"
              >
                <path
                  fill="currentColor"
                  d="M13.5 9H16l.5-3h-3V4.5c0-.8.2-1.5 1.5-1.5H16V0h-2c-2.5 0-4 1.5-4 4.2V6H8v3h2v9h3.5V9Z"
                />
              </svg>
              <span>Facebook</span>
            </a>
          </div>
        </div>

        {/* Contact */}
        <div className="ft-col" id="contact">
          <h4 className="ft-title">Contact</h4>
          <ul className="ft-contact">
            <li>
              <span className="ft-k">Email</span>
              <a href="mailto:allstarxperience@gmail.com">
                allstarxperience@gmail.com
              </a>
            </li>
            <li>
              <span className="ft-k">Phone</span>
              <a href="tel:+14387283726">+1 (438) 728-3726</a>
            </li>
            <li>
              <span className="ft-k">Phone</span>
              <a href="tel:+14385089880">+1 (438) 508-9880</a>
            </li>
            <li>
              <span className="ft-k">City</span>
              <span>Montreal, QC</span>
            </li>
          </ul>
        </div>

        {/* Quick Links */}
        <div className="ft-col explore-col">
          <h4 className="ft-title">Explore</h4>
          <ul className="ft-links">
            {links.map((l) => (
              <li key={l.label}>
                <a href={l.href}>{l.label}</a>
              </li>
            ))}
          </ul>
        </div>

        {/* Newsletter */}
        <div className="ft-col">
          <h4 className="ft-title">Stay in the Loop</h4>
          <p className="ft-note">News, fixtures, and registration reminders.</p>
          <SubscribeForm />
        </div>
      </div>

      <div className="footer-bottom">
        <div className="container footer-bottom-inner">
          <span>© {year} All Star Xperience. All rights reserved. Developed by Riachi.</span>
          <div className="ft-legal-links">
            <a href="/privacy">Privacy</a>
            <a href="/terms">Terms</a>
            <a href="#contact">Contact</a>
          </div>
        </div>
      </div>
    </footer>
  );
}


/* ---------- Services & Partners (Better layout) ---------- */
const SERVICES_FALLBACK = [
  { title: "Tournaments", description: "Quick paced tournaments over two days, consisting of group stages then play-offs. High risk, high reward.", icon_path: "/img/why/tournament.png" },
  { title: "Leagues", description: "Season-long leagues, more suitable for committed players who want to create a competitive environment on a weekly basis. Standings, stats & weekly player picks.", icon_path: "/img/why/league.png" },
  { title: "Institutional tournaments", description: "If you are an organization who wants to host a soccer tournament, we are your best bet! Event logistics, scheduling, and professional coverage are all available services we provide.", icon_path: "/img/why/org.png" },
];

const PARTNERS_FALLBACK = [
  { name: "Socceroof", logo_path: socceroofLogo, _bundled: true },
  { name: "Sports League Canada", logo_path: slcLogo, _bundled: true },
  { name: "Concordia Student Union", logo_path: concordiaLogo, _bundled: true },
  { name: "Electrolit", logo_path: electrolitLogo, _bundled: true },
  { name: "Slippio", logo_path: slippioLogo, _bundled: true },
];

function ServicesPartnersSection() {
  const { items: services } = useContent("services", SERVICES_FALLBACK);
  const { items: partners } = useContent("partners", PARTNERS_FALLBACK);

  return (
    <section className="section section-tight spBetter" id="services-partners">
      <div className="container">
        <div className="spBetter-head">
          <div>
            <h2 className="h2">Services & Partners</h2>
          </div>

          <div className="spBetter-actions">
            <a className="spBetter-btn spBetter-btn--primary" href="/register">
              Register
            </a>
            <a className="spBetter-btn spBetter-btn--ghost" href="/leagues">
              Leagues
            </a>
          </div>
        </div>

        <div className="card spBetter-card">
          <div className="spBetter-block">
            <div className="spBetter-blockTitle">Services</div>

            <div className="spBetter-services">
              {services.map((s) => {
                const icon = s.icon_path
                  ? (s.icon_path.startsWith("/img/") ? s.icon_path : assetUrl(s.icon_path))
                  : "";
                return (
                  <div className="spBetter-service" key={s.id ?? s.title}>
                    <div className="spBetter-ico" aria-hidden>
                      {icon ? (
                        <img
                          src={icon}
                          alt=""
                          className="spBetter-icoImg"
                          loading="lazy"
                        />
                      ) : null}
                    </div>
                    <div className="spBetter-serviceText">
                      <div className="spBetter-serviceTitle">{s.title}</div>
                      <div className="spBetter-serviceDesc">{s.description ?? s.desc}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="spBetter-sep" aria-hidden />

          <div className="spBetter-block">
            <div className="spBetter-blockTitle">Partners</div>

            <div className="spBetter-partnerCards">
              {partners.map((p) => {
                const src = p._bundled
                  ? p.logo_path
                  : (p.logo_path ? assetUrl(p.logo_path) : "");
                return (
                  <div className="spBetter-partner" key={p.id ?? p.name}>
                    <div className="spBetter-logoWrap" title={p.name}>
                      {src ? (
                        <img
                          className="spBetter-logo"
                          src={src}
                          alt={p.name}
                          loading="lazy"
                        />
                      ) : null}
                    </div>
                    <div className="spBetter-partnerName">{p.name}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const FALLBACK_TICKER = {
  "home.ticker.items":
    "Registrations are open\nKickoff this month\nFollow ASX schedule & results\nallstarxperience@gmail.com\n+1 (438)508-9880\n+1 (438) 728-3726\nFollow us on @allstarxperience",
  "home.ticker.speed_sec": "60",
};

/* ---------- Page ---------- */
export default function LandingPage() {
  // eslint-disable-next-line
  const { schedule, loading } = useGasApi(GAS_URL, GAS_KEY);

  const tickerSettings = useSettings("home.ticker.", FALLBACK_TICKER);

  /* Active event pages featured in the hero, in place of "Register Now" */
  const featuredEvents = useFeaturedEvents();

  const tickerItems = useMemo(() => {
    const lines = String(tickerSettings["home.ticker.items"] || "")
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    // Interleave a bullet between each item so the strip reads "A • B • C •"
    const interleaved = [];
    lines.forEach((text) => {
      interleaved.push({ text });
      interleaved.push({ text: "•" });
    });
    return interleaved;
  }, [tickerSettings]);
  const tickerSpeed = useMemo(() => {
    const n = parseInt(tickerSettings["home.ticker.speed_sec"], 10);
    return Number.isFinite(n) && n >= 5 && n <= 600 ? n : 60;
  }, [tickerSettings]);

  /* Normalize once from GAS */
  const matches = useMemo(() => {
    const base = Array.isArray(schedule) ? schedule : [];
    return base.map((m) => {
      const _start = parseLocalStart(m.date, m.time);
      const _status = getStatus(m); // "Upcoming" or "Final"
      return { ...m, _start, _status, _dateKey: dateKeyLocal(_start) };
    });
  }, [schedule]);

  const upcomingAll = useMemo(
    () => matches.filter((m) => m._status === "Upcoming").sort(byStart),
    [matches]
  );
  const finalsAsc = useMemo(
    () => matches.filter((m) => m._status === "Final").sort(byStart),
    [matches]
  );
  const finalsRecent = useMemo(() => [...finalsAsc].reverse(), [finalsAsc]);

  /* Banner selection */
  const nextUpcoming = useMemo(() => {
    const nowTs = Date.now();
    const future = upcomingAll
      .filter((m) => (m._start?.getTime?.() ?? 0) >= nowTs)
      .sort(byStart);
    return future[0] || upcomingAll[0] || null;
  }, [upcomingAll]);

  const selectedDateKey = useMemo(() => {
    if (nextUpcoming) return nextUpcoming._dateKey;
    if (finalsRecent.length) return finalsRecent[0]._dateKey;
    return matches.length ? matches[0]._dateKey : "";
  }, [nextUpcoming, finalsRecent, matches]);

  // eslint-disable-next-line
  const bannerDayMatches = useMemo(() => {
    const sameDay = matches.filter((m) => m._dateKey === selectedDateKey);
    const dayUpcoming = sameDay
      .filter((m) => m._status === "Upcoming")
      .sort(byStart);
    const dayFinals = sameDay
      .filter((m) => m._status === "Final")
      .sort(byStart);
    return [...dayUpcoming, ...dayFinals];
  }, [matches, selectedDateKey]);

  const nextMatch = nextUpcoming || null;

  /* Upcoming blocks: next 3 Prem, next 3 Div A */
  // eslint-disable-next-line
  const upPrem = useMemo(() => {
    const nowTs = Date.now();
    return upcomingAll
      .filter(
        (m) =>
          m.league === "Prem" && (m._start?.getTime?.() ?? 0) >= nowTs
      )
      .sort(byStart)
      .slice(0, 3);
  }, [upcomingAll]);

  // eslint-disable-next-line
  const upDiv = useMemo(() => {
    const nowTs = Date.now();
    return upcomingAll
      .filter(
        (m) =>
          (m.league === "Div" || m.league === "Div A") &&
          (m._start?.getTime?.() ?? 0) >= nowTs
      )
      .sort(byStart)
      .slice(0, 3);
  }, [upcomingAll]);

  /* Latest results (6) */
  // eslint-disable-next-line
  const latestResults = useMemo(
    () => finalsRecent.slice(0, 6),
    [finalsRecent]
  );

  /* Directions click handler (card-level) */
  // eslint-disable-next-line
  const directionsHref = nextMatch?.venue
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
        `${nextMatch.venue}, Montreal, QC`
      )}`
    : null;

  return (
    <>
      <SEO
        title="All Star Xperience | Montreal Soccer Leagues & Tournaments"
        description="Montreal's premier 7v7 soccer leagues and tournaments — competitive men's, women's and co-ed divisions with cash prizes, pro photos, and a welcoming community."
        keywords="Montreal soccer, 7v7 soccer Montreal, soccer league Montreal, soccer tournament Montreal, ASX, All Star Xperience, football Montreal, women's soccer Montreal, co-ed soccer Montreal"
      />
      <main className="lp-root">
        <BackgroundLottie />

        <div className="lp-content">
          {/* TICKER */}
          <div className="ticker">
            <div className="ticker-track">
              <Ticker
                durationSec={tickerSpeed}
                items={tickerItems}
              />
            </div>
          </div>

          {/* Banner (per-day)
          <GameStrip matches={bannerDayMatches} /> */}

          {/* HERO */}
          <section className="section hero">
            <div className="container hero-grid">
              <div>
                <div className="kicker">
                  <span className="kicker-dot" />
                  Montreal 7v7 Soccer
                </div>
                <h1 className="h1">
                  Montreal's Premier <span className="accent">7v7 Soccer Leagues</span> &amp; Tournaments
                </h1>
                <p style={{ marginTop: 12, marginBottom: 18, maxWidth: 640, color: "#475569", fontSize: 17, lineHeight: 1.5, fontWeight: 500 }}>
                  All Star Xperience runs Montreal's most competitive 7v7 soccer leagues and tournaments — men's, women's, and co-ed divisions with cash prizes, professional match photos, and a community that shows up. <strong>Play. Compete. Belong.</strong>
                </p>
                <div className="hero-cta" id="register">
                  {featuredEvents.length ? (
                    featuredEvents.map((ev) => (
                      <a
                        key={ev.id}
                        className="hero-event-btn"
                        href={`/e/${ev.slug}`}
                        style={
                          ev.accent_color
                            ? { "--hero-event-color": ev.accent_color }
                            : undefined
                        }
                      >
                        {ev.title}
                      </a>
                    ))
                  ) : (
                    <a className="btn btn-primary" href="/register">
                      Register Now
                    </a>
                  )}
                </div>
              </div>

              {/* Next Kickoff with Google Maps background & directions on click
              <div
                className="hero-card card hero-card--clickable"
                role="button"
                tabIndex={0}
                onClick={() => {
                  if (directionsHref)
                    window.open(
                      directionsHref,
                      "_blank",
                      "noopener,noreferrer"
                    );
                }}
                onKeyDown={(e) => {
                  if (
                    directionsHref &&
                    (e.key === "Enter" || e.key === " ")
                  )
                    window.open(
                      directionsHref,
                      "_blank",
                      "noopener,noreferrer"
                    );
                }}
                title={
                  nextMatch?.venue
                    ? `Open directions to ${nextMatch.venue}`
                    : "Next Kickoff"
                }
              >
                {nextMatch?.venue ? (
                  <GMapBackdrop
                    placeQuery={`${nextMatch.venue}, Montreal, QC`}
                  />
                ) : null}

                <div className="fake-panel panel-on-map">
                  {nextMatch ? (
                    <div>
                      <h3 className="panel-title">Next Kickoff</h3>
                      <p className="panel-sub">
                        {fmtDate(nextMatch._start)} ·{" "} {nextMatch.venue} ·{" "}
                        <strong>
                          {formatLeagueLabel(nextMatch.league)}
                        </strong>
                      </p>
                      <a
                        className="panel-directions"
                        href={directionsHref || "#"}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Get Directions ↗
                      </a>
                    </div>
                  ) : (
                    <div>
                      <h3 className="panel-title">Season Kickoff</h3>
                      <p className="panel-sub">Fixtures will appear here.</p>
                    </div>
                  )}
                </div>
              </div> */}
            </div>
          </section>

          {/* WHY JOIN */}
          <section className="section section-tight" id="why">
            <div className="container">
              <h2 className="h2" style={{ marginBottom: 20 }}>
                Why join All Star Xperience?
              </h2>
              <WhyJoinDeck />
            </div>
          </section>

          {/* SERVICES & PARTNERS */}
          <ServicesPartnersSection />

          {/* MATCHES */}
          {/* <section className="section">
            <div className="container">
              <div className="matches-header">
                <h2 className="h2">Matches</h2>
                <div className="tabs">
                  <span className="tab active">
                    Upcoming ({upcomingAll.length})
                  </span>
                  <span className="tab">Results ({finalsAsc.length})</span>
                </div>
              </div> */}

              {/* Upcoming — Prem (next 3) */}
              {/* <h3 className="h3-sub">Prem — Next 3</h3>
              <div className="cards" style={{ marginBottom: 22 }}>
                {upPrem.map((m, i) => (
                  <MatchCard key={`UP-${i}`} match={m} mode="upcoming" />
                ))}
                {!loading && upPrem.length === 0 && (
                  <div
                    className="card"
                    style={{ padding: 16, color: "#475569" }}
                  >
                    No upcoming Prem matches.
                  </div>
                )}
              </div> */}

              {/* Upcoming — Div A (next 3) */}
              {/* <h3 className="h3-sub">Div A — Next 3</h3>
              <div className="cards" style={{ marginBottom: 22 }}>
                {upDiv.map((m, i) => (
                  <MatchCard key={`UD-${i}`} match={m} mode="upcoming" />
                ))}
                {!loading && upDiv.length === 0 && (
                  <div
                    className="card"
                    style={{ padding: 16, color: "#475569" }}
                  >
                    No upcoming Div A matches.
                  </div>
                )}
              </div> */}

              {/* Recent results */}
              {/* <div className="matches-header" style={{ marginTop: 8 }}>
                <h2 className="h2">Recent Results</h2>
              </div>
              <div className="cards">
                {latestResults.map((m, i) => (
                  <MatchCard key={`F-${i}`} match={m} mode="final" />
                ))}
                {!loading && latestResults.length === 0 && (
                  <div
                    className="card"
                    style={{ padding: 16, color: "#475569" }}
                  >
                    No results yet.
                  </div>
                )}
              </div>
            </div>
          </section> */}
        </div>
      </main>

      <ReviewsCarousel />
      <Footer />
    </>
  );
}

/* ---------- helpers ---------- */
function formatLeagueLabel(league = "") {
  return league === "Div" ? "Div A" : league;
}

/* ---------- Presentational subcomponents ---------- */
// eslint-disable-next-line
function MatchCard({ match, mode }) {
  const start = match._start ?? new Date();
  const isFinal = mode === "final";
  const leagueLabel = formatLeagueLabel(match.league);

  return (
    <article className={`match-card ${isFinal ? "is-final" : ""}`}>
      <div className="mc-head mc-head--stack">
        <div className="mc-head-left mc-when">
          <span className="mc-date">{fmtDate(start)}</span>
          <span
            className="mc-time"
            style={{ color: "#64748b", fontWeight: 600 }}
          >
            {fmtTime(match.time)}
          </span>
        </div>
        <span
          className={`mc-league ${
            leagueLabel === "Prem" ? "prem" : "div"
          }`}
        >
          {leagueLabel}
        </span>
      </div>

      <div className="mc-rows">
        <div className="mc-row">
          <TeamBadge name={match.home} />
          <span className="mc-name">{match.home}</span>
          {isFinal && (
            <span className="mc-score">{match.home_score ?? ""}</span>
          )}
        </div>
        <div className="mc-row">
          <TeamBadge name={match.away} />
          <span className="mc-name">{match.away}</span>
          {isFinal && (
            <span className="mc-score">{match.away_score ?? ""}</span>
          )}
        </div>
      </div>

      <div className="mc-foot">
        <span className="mc-venue">{match.venue}</span>
        {!isFinal && <span className="mc-status">Upcoming</span>}
      </div>
    </article>
  );
}

function TeamBadge({ name }) {
  const initials = getInitials(name);
  const colorClass = pickColorClass(name);
  return (
    <div className={`badge-logo ${colorClass}`} title={name}>
      {initials}
    </div>
  );
}

function getInitials(str = "") {
  const words = str
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) return "??";
  const first = words[0][0] || "";
  const second = words.length > 1 ? words[1][0] : words[0][1] || "";
  return (first + second).toUpperCase();
}
function pickColorClass(str = "") {
  let h = 0;
  for (let i = 0; i < str.length; i++)
    h = (h * 33 + str.charCodeAt(i)) % 9973;
  const n = (h % 6) + 1;
  return `color-${n}`;
}
